import { DocumentNode, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import React, { useContext, useMemo, useRef } from 'react';
import WorkspaceContext from '../Context/WorkspaceContext';
import ViewContext from '../Context/ViewContext';
import FilteredProductAttributeReferencesQuery from '../../graphql/queries/product/FilteredProductAttributeReferences.graphql';
import FilteredPriceListAttributeReferences from '../../graphql/queries/product/FilteredPriceListAttributeReferences.graphql';
import FilteredSalesLineProductAttributeReferencesQuery from '../../graphql/queries/sales/FilteredSalesLineProductAttributeReferences.graphql';
import FilteredCustomerAttributeReferencesQuery from '../../graphql/queries/customer/FilteredCustomerAttributeReferences.graphql';
import FilteredSupplierAttributeReferencesQuery from '../../graphql/queries/supplier/FilteredSupplierAttributeReferences.graphql';
import FilteredSalesAttributeReferencesQuery from '../../graphql/queries/sales/FilteredSalesAttributeReferences.graphql';
import FilteredPurchaseAttributeReferencesQuery from '../../graphql/queries/purchases/FilteredPurchaseAttributeReferences.graphql';
import FilteredDiaryAttributeReferencesQuery from '../../graphql/queries/diary/FilteredDiaryAttributeReferences.graphql';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { default as Form } from '../connectedForm';
import FluentList from '../FluentWrapper/FluentList';
import { viewActions } from '../../state/modules/view';
import {
  IAttributeReference,
  ICustomerAttributeConfig,
  IDiaryAttributeConfig,
  IProductAttributeConfig,
  IPurchaseAttributeConfig,
  ISalesAttributeConfig,
  ISupplierAttributeConfig,
} from '../../graphql/schema/p4types';
import { IColumn } from '@fluentui/react/src/components/DetailsList/DetailsList.types';

type IAttributeConfig =
  | IProductAttributeConfig
  | ICustomerAttributeConfig
  | ISupplierAttributeConfig
  | IDiaryAttributeConfig
  | ISalesAttributeConfig
  | IPurchaseAttributeConfig;

const useFilteredAttributeReferenceQuery = (attributeConfig: IAttributeConfig, rightAttributeObjects: Array<any>) => {
  const { workspaceFilters } = useContext(WorkspaceContext);
  const { viewScreenName } = useContext(ViewContext);

  let query: DocumentNode;
  switch (attributeConfig.type) {
    case 'productAttributeConfig':
      if (viewScreenName && viewScreenName.includes('OrderLine')) {
        query = FilteredSalesLineProductAttributeReferencesQuery;
      } else if (viewScreenName && viewScreenName.includes('PriceList')) {
        query = FilteredPriceListAttributeReferences;
      } else {
        query = FilteredProductAttributeReferencesQuery;
      }
      break;
    case 'customerAttributeConfig':
      query = FilteredCustomerAttributeReferencesQuery;
      break;
    case 'supplierAttributeConfig':
      query = FilteredSupplierAttributeReferencesQuery;
      break;
    case 'diaryAttributeConfig':
      query = FilteredDiaryAttributeReferencesQuery;
      break;
    case 'salesAttributeConfig':
      query = FilteredSalesAttributeReferencesQuery;
      break;
    case 'purchaseAttributeConfig':
      query = FilteredPurchaseAttributeReferencesQuery;
      break;
    default:
      throw new Error('Attribute config type not recognized.');
  }

  const filters = useMemo(() => {
    if (Array.isArray(workspaceFilters)) {
      return workspaceFilters.filter((workspaceFilter) => {
        const filterParameters = workspaceFilter?.filterParameters?.filters?.[0]?.filterParameters;
        if (
          !filterParameters ||
          (filterParameters.field !== 'attributeValues.attributeConfig.title' &&
            filterParameters.field !== 'product.attributeValues.attributeConfig.title') //SOLine Workspace uses product attributes nested like this.
        ) {
          return true;
        }
        return !rightAttributeObjects.find((rao) => rao.label === filterParameters.value);
      });
    }
    return [];
  }, [rightAttributeObjects, workspaceFilters]);

  return useQuery(query as DocumentNode, {
    variables: normalizeVariables({
      filters: filters,
      [attributeConfig.type]: attributeConfig,
    }),
    skip: !query || !attributeConfig || !workspaceFilters || workspaceFilters.length === 0,
  });
};

/**
 * @param attributeObject - The attribute filter from the current view.
 * @param attributeConfig - The attribute config for the this attribute (e.g. 'ProductAttributeConfig').
 * @param cols            - Relative width for the list.
 * @param rightAttributeObjects  - Any list filter attributes to the 'right' need to be excluded from the query (Includes self)
 * @returns {JSX.Element}
 * @constructor
 */
const FilterList = ({
  attributeObject,
  attributeConfig,
  cols,
  rightAttributeObjects,
}: {
  attributeObject: any;
  attributeConfig: IAttributeConfig;
  cols: number;
  rightAttributeObjects: Array<any>;
}) => {
  const dispatch = useDispatch();
  const { currentView, viewScreenName } = useContext(ViewContext);

  const { data, loading } = useFilteredAttributeReferenceQuery(attributeConfig, rightAttributeObjects);
  const prevAttributeReferenceList = useRef();

  const attributeReferenceList = useMemo(() => {
    //If filters change, the list is re-queried. To avoid a brief 'flicker', we fallback on the previous value while the new query is loading.
    if (loading && prevAttributeReferenceList.current) return prevAttributeReferenceList.current;
    const list = data?.listData || attributeConfig.attributeReferenceList;
    const sortedList = [...list].sort((a, b) => (a.description > b.description ? 1 : -1));
    prevAttributeReferenceList.current = sortedList;
    return sortedList;
  }, [attributeConfig.attributeReferenceList, data?.listData, loading]);
  const columns = useMemo<Array<IColumn>>(() => {
    return [
      {
        key: 'column1',
        name: attributeObject.label,
        fieldName: 'description',
      },
    ];
  }, [attributeObject.label]);

  const selectedAttributesFromView =
    attributeObject?.objects?.map((object: { id: number }) =>
      attributeReferenceList.find((ar: IAttributeReference) => ar.id === object.id)
    ) || [];

  if (currentView.hideConfiguration) return null;

  if (!attributeReferenceList) return null;

  return (
    <Form.Fieldset cols={cols}>
      <div style={{ height: window.innerHeight < 800 ? 200 : 300, position: 'relative' }}>
        <FluentList
          items={attributeReferenceList}
          columns={columns}
          selectedItems={selectedAttributesFromView}
          onItemsSelected={(selectedItems) => {
            dispatch(
              viewActions.updateFilter(viewScreenName, {
                ...attributeObject,
                objects: selectedItems.length > 0 ? selectedItems : null,
              })
            );
          }}
          enableRowSelect={true}
          enableLocalSearch={true}
        />
      </div>
    </Form.Fieldset>
  );
};

const TARGET_LIST_WIDTH = 300;

export const FilterListsView = ({ contentWidth }: { contentWidth: number }) => {
  const { currentView, availableColumns } = useContext(ViewContext);

  //Figure out how many columns we can sensible fit in the available with (Minimum of 3).
  const maximumColumns = useMemo(
    () => (contentWidth / TARGET_LIST_WIDTH < 3 ? 3 : Math.floor(contentWidth / TARGET_LIST_WIDTH)),
    [contentWidth]
  );
  const filteredAttributeObjects = useMemo(() => {
    return (currentView?.filterAttributeObjects || []).filter((filterAttributeObject: any) => {
      return filterAttributeObject?.filterStyle === 'LIST';
    });
  }, [currentView?.filterAttributeObjects]);

  const lists = useMemo(() => {
    //Figure out the relative column sizes - Whichever is smaller out of maximum columns & cols per list.
    const colsPerList =
      1 / (filteredAttributeObjects.length < maximumColumns ? maximumColumns : filteredAttributeObjects.length);

    return filteredAttributeObjects.map((filterAttributeObject: any, index: number) => {
      const thisAttributeConfig = availableColumns.find((col: any) => col.fieldName === filterAttributeObject.fieldName)
        .options.attributeConfig;
      return (
        <FilterList
          key={`filter_list_${index}`}
          attributeObject={filterAttributeObject}
          attributeConfig={thisAttributeConfig}
          cols={colsPerList}
          rightAttributeObjects={filteredAttributeObjects.slice(index)}
        />
      );
    });
  }, [availableColumns, filteredAttributeObjects, maximumColumns]);

  if (lists.length === 0) return null;

  if (maximumColumns < filteredAttributeObjects.length) {
    return (
      <div style={{ width: `${contentWidth}px`, overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <div
          style={{
            width: `${filteredAttributeObjects.length * TARGET_LIST_WIDTH}px`,
            paddingLeft: '5px',
            borderBottomWidth: '1px',
            borderBottomColor: '#e1e1e1',
            borderBottomStyle: 'solid',
          }}
        >
          <Form.Row>{lists}</Form.Row>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: `${contentWidth}px`,
        paddingLeft: '5px',
        borderBottomWidth: '1px',
        borderBottomColor: '#e1e1e1',
        borderBottomStyle: 'solid',
      }}
    >
      <Form.Row>{lists}</Form.Row>
    </div>
  );
};
